/**
 * Custom hook to manage sidebar collapse state
 *
 * Features:
 * - Persists state to localStorage
 * - Auto-collapses on mobile (<768px)
 * - Provides toggle function
 *
 * Usage:
 * const { isCollapsed, toggle } = useSidebarCollapse();
 */
import { useState, useEffect } from 'react';

const STORAGE_KEY = 'sidebar-collapsed';
const MOBILE_BREAKPOINT = 768;

export const useSidebarCollapse = () => {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    // Check localStorage first
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      return JSON.parse(stored);
    }

    // Default: SEMPRE iniciar colapsado (conforme solicitação do usuário)
    return true;
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  // Handle window resize - auto-collapse on mobile
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
      if (isMobile && !isCollapsed) {
        setIsCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isCollapsed]);

  const toggle = () => {
    setIsCollapsed((prev) => !prev);
  };

  return {
    isCollapsed,
    toggle,
  };
};
